import "./GeneratePage.scss";

import { Select, message } from "antd";
import { intervals } from "./intervals";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import dayjsTZ from "../../../utils/dayjs";
import { getTranslation } from "./translations";
import { useLayoutEffect, useState } from "react";
import { CaretDownFilled } from "@ant-design/icons";
import { Collapse, DatePicker, Tooltip } from "antd";
import ReportPersonalizer from "./ReportPersonalizer";
import { getTemplateOptions, useReportTemplates } from "./reportTemplates";
import { AutoCompleteIcon, LeftArrow } from "../../../assets/icons";
import { showWarningMsg } from "../../Documentation/utils/Messages";
import MondayButton from "../../commonComponents/MondayButton/MondayButton";

const { Option } = Select;
const { Panel } = Collapse;

const PERIOD_OPTIONS = [
  { value: "range", label: "Interval" },
  { value: "today", label: "Sot" },
  { value: "yesterday", label: "Dje" },
  { value: "thisWeek", label: "Këtë javë" },
  { value: "thisMonth", label: "Këtë muaj" },
  { value: "previousMonth", label: "Muaji i kaluar" },
  { value: "thisYear", label: "Këtë vit" },
];

function GeneratePageHeader({
  contentData,
  updateColumns,
  filterReportsClockings,
}) {
  const navigate = useNavigate();

  const { isDarkMode } = useSelector((state) => state.usersList);
  const { employees } = useSelector((state) => state.employeesList);
  const { templates, activeTemplateId, switchTemplate } = useReportTemplates();

  const [filters, setFilters] = useState({
    filterType: "range",
    default: true,
    timeInterval: intervals["thisMonth"],
    employeeId: null,
    employeeFullName: null,
  });
  const [spin, setSpin] = useState(false);
  const [shortcut, setShortcut] = useState("range");
  const [openPersonalizer, setOpenPersonalizer] = useState(false);
  const [switchingTemplate, setSwitchingTemplate] = useState(false);
  const [columns, setColumns] = useState(contentData?.selectedColumns || []);

  //region UPDATE FILTERS
  const canGenerate =
    !!filters.timeInterval?.[0] && !!filters.timeInterval?.[1];

  const updateFilters = async () => {
    if (!canGenerate) return;
    setSpin(true);
    try {
      await filterReportsClockings(filters);
      updateColumns(!!columns.length ? columns : null);
    } catch (err) {
      console.error("Failed to generate report:", err);
    } finally {
      setSpin(false);
    }
  };

  const onTemplateApplied = () => updateColumns(null);

  const onTemplateSwitch = async (templateId) => {
    setSwitchingTemplate(true);
    try {
      await switchTemplate(templateId);
      onTemplateApplied();
    } catch (err) {
      message.error("Ka ndodhur një gabim gjatë ndryshimit të shabllonit");
      console.error("Error switching report template", err);
    } finally {
      setSwitchingTemplate(false);
    }
  };

  useLayoutEffect(() => {
    if (!!contentData?.selectedColumns) {
      setColumns(contentData?.selectedColumns || []);
    }
  }, [contentData]);

  //region RETURN
  return (
    <div className="generate-page-header">
      <Collapse
        className="card-report-header reports-collapse"
        title="Gjenero raportin"
        defaultActiveKey={["1"]}
        style={{ width: "100%" }}
        expandIconPosition="right"
        expandIcon={({ isActive }) => (
          <CaretDownFilled rotate={isActive ? 0 : 90} />
        )}
      >
        <Panel
          key={1}
          header={
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Tooltip title="Kthehu pas">
                <MondayButton
                  className="mondayButtonGrey"
                  onClick={() => navigate("/raportet")}
                  Icon={<LeftArrow fill="#1d3445" />}
                />
              </Tooltip>
              Gjenero raportin
            </div>
          }
        >
          <div className="content-header-card">
            <div className="time-period">
              {!!templates.length ? (
                <div className="filter-row">
                  <span>Shablloni:</span>
                  <Select
                    suffixIcon={<CaretDownFilled />}
                    className="select-option"
                    popupClassName={isDarkMode ? "darkDropdown" : ""}
                    variant={false}
                    loading={switchingTemplate}
                    disabled={switchingTemplate}
                    value={activeTemplateId}
                    onChange={onTemplateSwitch}
                    options={getTemplateOptions(templates)}
                  />
                </div>
              ) : null}
              <div className="filter-row">
                <span>Punonjësi:</span>
                <Select
                  showSearch
                  allowClear
                  style={{ minWidth: 200, width: "100%" }}
                  suffixIcon={<CaretDownFilled />}
                  className="select-option"
                  popupClassName={isDarkMode ? "darkDropdown" : ""}
                  variant={false}
                  placeholder="Zgjidh punonjësin"
                  value={filters.employeeId}
                  onChange={(e, item) => {
                    setFilters((prev) => ({
                      ...prev,
                      employeeId: !!e ? item?.employeeId : null,
                      employeeFullName: !!e ? item?.children : null,
                    }));
                  }}
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {(employees || []).map((el) => {
                    const fullName = `${el.employeeFirstName} ${el.employeeLastName}`;
                    return (
                      <Option
                        key={el.employeeId}
                        value={el.employeeId}
                        employeeId={el.employeeId}
                      >
                        {fullName}
                      </Option>
                    );
                  })}
                </Select>
              </div>
              <div className="filter-row">
                <span>Periudha:</span>
                <Select
                  suffixIcon={<CaretDownFilled />}
                  className="select-option"
                  popupClassName={isDarkMode ? "darkDropdown" : ""}
                  variant={false}
                  value={shortcut}
                  onChange={(e) => {
                    setShortcut(e);
                    setFilters((prev) => ({
                      ...prev,
                      filterType: e,
                      ...(e !== "range" ? { timeInterval: intervals[e] } : {}),
                    }));
                  }}
                >
                  {PERIOD_OPTIONS.map(({ value, label }) => (
                    <Option key={value} value={value}>
                      {label}
                    </Option>
                  ))}
                </Select>
                <DatePicker
                  placeholder="Fillimi"
                  value={filters.timeInterval?.[0]}
                  popupClassName={isDarkMode ? "darkDropdown" : ""}
                  onChange={(val) => {
                    setFilters((prev) => {
                      const [_, end] = prev.timeInterval || [];
                      let timeInterval = [val, end];
                      if (end && val && dayjsTZ(val).isAfter(end)) {
                        timeInterval = [val, null];
                      }
                      return { ...prev, timeInterval };
                    });
                    !!filters.timeInterval?.[1] && setShortcut("range");
                  }}
                  allowClear={false}
                  format={"DD/MM/YYYY"}
                />
                <DatePicker
                  placeholder="Mbarimi"
                  value={filters.timeInterval?.[1]}
                  popupClassName={isDarkMode ? "darkDropdown" : ""}
                  onChange={(val) => {
                    setFilters((prev) => {
                      const [start, _] = prev.timeInterval || [];
                      let timeInterval = [start, val];
                      if (start && val && dayjsTZ(val).isBefore(start)) {
                        timeInterval = [start, null];
                        showWarningMsg({
                          content:
                            "Data e mbarimit duhet të jetë pas datës së fillimit.",
                        });
                      }
                      return { ...prev, timeInterval };
                    });
                    !!filters.timeInterval?.[0] && setShortcut("range");
                  }}
                  allowClear={false}
                  format={"DD/MM/YYYY"}
                />
              </div>
              {!!contentData?.allColumns?.length ? (
                <div className="filter-row" style={{ flex: 1 }}>
                  <span>Kolonat:</span>
                  <Select
                    mode="multiple"
                    showSearch
                    allowClear={false}
                    className="select-option-multi"
                    variant={false}
                    popupClassName={isDarkMode ? "darkDropdown" : ""}
                    placeholder="Please select"
                    value={columns}
                    onChange={(e) => setColumns(e)}
                    maxTagCount={"responsive"}
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      String(option?.children ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {contentData?.allColumns?.map((el) => (
                      <Option key={el} value={el}>
                        {getTranslation(el)}
                      </Option>
                    ))}
                  </Select>
                  {!!contentData?.allColumns?.length &&
                  !!contentData?.columnsDef?.length &&
                  columns?.length !== contentData?.selectedColumns?.length ? (
                    <Tooltip title="Përditëso kolonat">
                      <MondayButton
                        onClick={() =>
                          updateColumns(!!columns.length ? columns : null)
                        }
                        className="mondayButtonGreen"
                        Icon={<AutoCompleteIcon />}
                      />
                    </Tooltip>
                  ) : null}
                </div>
              ) : null}
              <div style={{ display: "flex", gap: 10 }}>
                <Tooltip
                  title={
                    !canGenerate
                      ? "Zgjidhni intervalin e plotë (fillimi dhe mbarimi)"
                      : ""
                  }
                >
                  <MondayButton
                    spin={spin}
                    disabled={!canGenerate}
                    onClick={updateFilters}
                  >
                    Gjenero
                  </MondayButton>
                </Tooltip>
              </div>
              <MondayButton
                className="mondayButtonBlue"
                onClick={() => setOpenPersonalizer(true)}
              >
                Personalizo
              </MondayButton>
            </div>
          </div>
        </Panel>
      </Collapse>
      {openPersonalizer ? (
        <ReportPersonalizer
          open={openPersonalizer}
          setOpen={setOpenPersonalizer}
          onTemplateApplied={onTemplateApplied}
        />
      ) : null}
    </div>
  );
}

export default GeneratePageHeader;
